import React, { useState, useEffect } from 'react';
import { getInvitations, sendInvitation, getTeamStats, resendInvitation, cancelInvitation, adminResetPassword } from '../services/api';
import Layout from '../components/Layout';
import { showSuccess, showError, showInfo } from '../services/swal';

const Team = () => {
    const [invitations, setInvitations] = useState([]);
    const [members, setMembers] = useState([]);
    const [stats, setStats] = useState({
        total_members: 0,
        pending_invites: 0,
        roles: []
    });
    const [activeTab, setActiveTab] = useState('members');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        role: 'salesman'
    });
    const [loading, setLoading] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Password Reset State
    const [showResetModal, setShowResetModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    useEffect(() => {
        fetchTeamData();
    }, []);

    const fetchTeamData = async () => {
        setLoading(true);
        try {
            const [teamRes, statsRes] = await Promise.all([
                getInvitations(),
                getTeamStats()
            ]);
            setInvitations(teamRes.data.invitations || []);
            setMembers(teamRes.data.members || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching team data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await sendInvitation(formData);
            showSuccess('Invitation Sent!', `An email has been sent to ${formData.email}.`);
            setInviteLink(response.data.invitation_link);
            fetchTeamData();
            setFormData({ email: '', role: 'salesman' });
        } catch (error) {
            showError('Invitation Failed', error.response?.data?.message || 'Failed to send invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async (id) => {
        try {
            await resendInvitation(id);
            showSuccess('Resent!', 'Invitation email has been sent again.');
        } catch (error) {
            showError('Failed', 'Could not resend invitation.');
        }
    };

    const handleCancel = async (id) => {
        try {
            await cancelInvitation(id);
            showSuccess('Cancelled', 'Invitation has been revoked.');
            fetchTeamData();
        } catch (error) {
            showError('Failed', 'Could not cancel invitation.');
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(inviteLink);
        showInfo('Copied!', 'Invitation link copied to clipboard.');
    };

    const handleOpenResetModal = (member) => {
        setSelectedMember(member);
        setNewPassword('');
        setConfirmPassword('');
        setShowResetModal(true);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) {
            showError('Invalid Password', 'Password must be at least 8 characters long.');
            return;
        }
        if (newPassword !== confirmPassword) {
            showError('Password Mismatch', 'Passwords do not match.');
            return;
        }
        setResetLoading(true);
        try {
            const response = await adminResetPassword(selectedMember.id, {
                password: newPassword,
                password_confirmation: confirmPassword
            });
            showSuccess('Password Updated!', response.data.message || `Password for ${selectedMember.name} has been reset.`);
            setShowResetModal(false);
            setSelectedMember(null);
        } catch (error) {
            showError('Reset Failed', error.response?.data?.message || 'Failed to reset employee password.');
        } finally {
            setResetLoading(false);
        }
    };

    const getRoleBadge = (role) => {
        const roles = {
            admin: { color: 'danger', icon: 'shield-lock-fill' },
            manager: { color: 'warning', icon: 'person-badge-fill' },
            salesman: { color: 'primary', icon: 'cart-fill' },
            accountant: { color: 'info', icon: 'calculator-fill' }
        };
        const config = roles[role?.toLowerCase()] || { color: 'secondary', icon: 'person-fill' };
        return (
            <span className={`badge rounded-pill bg-${config.color} bg-opacity-10 text-${config.color} border border-${config.color} border-opacity-25 px-3 py-2 fw-bold`}>
                <i className={`bi bi-${config.icon} me-1`}></i>
                {role?.toUpperCase()}
            </span>
        );
    };

    const filteredMembers = members.filter(m => 
        m.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        m.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredInvites = invitations.filter(i => 
        i.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout title="Team Management">
            {/* Header Analytics */}
            <div className="row g-3 g-md-4 mb-4">
                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm overflow-hidden h-100" style={{ borderRadius: '24px' }}>
                        <div className="card-body p-4 position-relative">
                            <div className="d-flex align-items-center mb-3">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-4 p-3 me-3">
                                    <i className="bi bi-people-fill fs-3"></i>
                                </div>
                                <div>
                                    <h6 className="text-muted fw-bold mb-0">Total Team</h6>
                                    <h3 className="fw-bold mb-0">{stats.total_members}</h3>
                                </div>
                            </div>
                            <div className="progress" style={{ height: '6px', borderRadius: '10px' }}>
                                <div className="progress-bar bg-primary" style={{ width: '100%' }}></div>
                            </div>
                            <small className="text-muted mt-2 d-block">Active personnel in your shop</small>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm overflow-hidden h-100" style={{ borderRadius: '24px' }}>
                        <div className="card-body p-4 position-relative">
                            <div className="d-flex align-items-center mb-3">
                                <div className="bg-success bg-opacity-10 text-success rounded-4 p-3 me-3">
                                    <i className="bi bi-patch-check-fill fs-3"></i>
                                </div>
                                <div>
                                    <h6 className="text-muted fw-bold mb-0">Onboarding</h6>
                                    <h3 className="fw-bold mb-0">{stats.pending_invites}</h3>
                                </div>
                            </div>
                            <div className="progress" style={{ height: '6px', borderRadius: '10px' }}>
                                <div className="progress-bar bg-success" style={{ width: '60%' }}></div>
                            </div>
                            <small className="text-muted mt-2 d-block">Pending invitation approvals</small>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm overflow-hidden h-100" style={{ borderRadius: '24px', background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)' }}>
                        <div className="card-body p-4 text-white">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h5 className="fw-bold mb-1">Grow Your Team</h5>
                                    <p className="small opacity-75 mb-3">Scale your operations by adding experts.</p>
                                    <button 
                                        className="btn btn-white bg-white text-primary rounded-pill fw-bold px-4 shadow-sm"
                                        onClick={() => { setShowInviteModal(true); setInviteLink(''); }}
                                    >
                                        <i className="bi bi-plus-lg me-2"></i>Invite Member
                                    </button>
                                </div>
                                <i className="bi bi-person-plus opacity-25 d-none d-lg-block" style={{ fontSize: '4rem' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="card border-0 shadow-sm overflow-hidden mb-5" style={{ borderRadius: '24px' }}>
                <div className="card-header bg-white border-0 p-4 pb-0 pb-lg-4">
                    <div className="row g-3 align-items-center">
                        <div className="col-12 col-lg-6">
                            <div className="nav nav-pills gap-1 bg-light p-1 rounded-4 d-flex" style={{ width: '100%' }}>
                                <button 
                                    className={`nav-link flex-grow-1 rounded-4 fw-bold py-2 ${activeTab === 'members' ? 'active shadow-sm' : 'text-muted'}`}
                                    onClick={() => setActiveTab('members')}
                                >
                                    Active Team
                                </button>
                                <button 
                                    className={`nav-link flex-grow-1 rounded-4 fw-bold py-2 ${activeTab === 'invitations' ? 'active shadow-sm' : 'text-muted'}`}
                                    onClick={() => setActiveTab('invitations')}
                                >
                                    Pending Invites
                                </button>
                            </div>
                        </div>
                        <div className="col-12 col-lg-6">
                            <div className="d-flex align-items-center bg-light rounded-4 px-3 border border-2 border-transparent transition-all focus-within-primary" style={{ height: '48px' }}>
                                <i className="bi bi-search text-muted me-2"></i>
                                <input 
                                    type="text" 
                                    className="form-control bg-transparent border-0 shadow-none p-0 fw-medium search-input-clean" 
                                    placeholder="Search team members..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-body p-0">
                    {/* Desktop View */}
                    <div className="table-responsive d-none d-lg-block">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4 py-3 text-muted small fw-bold text-uppercase">Team Member</th>
                                    <th className="py-3 text-muted small fw-bold text-uppercase">Access Role</th>
                                    <th className="py-3 text-muted small fw-bold text-uppercase">Contact Trace</th>
                                    <th className="py-3 text-muted small fw-bold text-uppercase">Join Date</th>
                                    <th className="text-end pe-4 py-3 text-muted small fw-bold text-uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeTab === 'members' ? (
                                    filteredMembers.length > 0 ? (
                                        filteredMembers.map(member => (
                                            <tr key={member.id} className="transition-all hover-light">
                                                <td className="ps-4 py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold shadow-sm" style={{ width: '45px', height: '45px', fontSize: '1.2rem' }}>
                                                            {member.name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark">{member.name}</div>
                                                            <div className="small text-muted">{member.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{getRoleBadge(member.role)}</td>
                                                <td>
                                                    <div className="small fw-medium"><i className="bi bi-telephone me-2 opacity-50"></i>{member.phone_number || 'N/A'}</div>
                                                    <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}><i className="bi bi-geo-alt me-2 opacity-50"></i>{member.address || 'Address not set'}</div>
                                                </td>
                                                <td>
                                                    <div className="small fw-bold text-dark">{new Date(member.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                                    <div className="small text-success fw-medium">Active Status</div>
                                                </td>
                                                <td className="text-end pe-4">
                                                     <div className="dropdown">
                                                         <button className="btn btn-light btn-sm rounded-circle shadow-sm" data-bs-toggle="dropdown">
                                                             <i className="bi bi-three-dots-vertical"></i>
                                                         </button>
                                                         <ul className="dropdown-menu dropdown-menu-end border-0 shadow-lg rounded-4 p-2">
                                                             <li><button className="dropdown-item rounded-3 py-2 text-primary"><i className="bi bi-pencil-square me-2"></i> Edit Profile</button></li>
                                                             <li><button className="dropdown-item rounded-3 py-2 text-warning" onClick={() => handleOpenResetModal(member)}><i className="bi bi-key me-2"></i> Reset Password</button></li>
                                                             <li><button className="dropdown-item rounded-3 py-2 text-secondary"><i className="bi bi-shield-lock me-2"></i> Permissions</button></li>
                                                             <li><hr className="dropdown-divider opacity-50" /></li>
                                                             <li><button className="dropdown-item rounded-3 py-2 text-danger"><i className="bi bi-trash3 me-2"></i> Remove Team</button></li>
                                                         </ul>
                                                     </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="5" className="text-center py-5 text-muted">No active members found matching your search.</td></tr>
                                    )
                                ) : (
                                    filteredInvites.length > 0 ? (
                                        filteredInvites.map(inv => (
                                            <tr key={inv.id} className="transition-all hover-light">
                                                <td className="ps-4 py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-light text-muted rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm border" style={{ width: '45px', height: '45px' }}>
                                                            <i className="bi bi-envelope-at"></i>
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark">{inv.email}</div>
                                                            <div className="small text-muted">Sent by: {inv.inviter?.name || 'Admin'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{getRoleBadge(inv.role)}</td>
                                                <td><span className="badge bg-warning bg-opacity-10 text-warning px-3 py-2 rounded-pill fw-bold border border-warning border-opacity-25"><i className="bi bi-hourglass-split me-1"></i> PENDING ACCEPTANCE</span></td>
                                                <td><small className="text-muted">{new Date(inv.created_at).toLocaleDateString()}</small></td>
                                                <td className="text-end pe-4">
                                                    <button className="btn btn-sm btn-light border rounded-pill px-3 me-2 shadow-sm fw-bold" onClick={() => handleResend(inv.id)}>
                                                        <i className="bi bi-arrow-repeat me-1"></i> Resend
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger rounded-pill px-3 shadow-sm fw-bold" onClick={() => handleCancel(inv.id)}>Cancel</button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="5" className="text-center py-5 text-muted">No pending invitations matching your search.</td></tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View */}
                    <div className="d-lg-none p-3 bg-light bg-opacity-25">
                        {activeTab === 'members' ? (
                            filteredMembers.map(member => (
                                <div key={member.id} className="card border-0 shadow-sm rounded-4 mb-3 p-3 transition-all active-scale">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold shadow-sm" style={{ width: '50px', height: '50px', fontSize: '1.3rem' }}>
                                                {member.name?.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="fw-bold text-dark h6 mb-0">{member.name}</div>
                                                <div className="small text-muted">{member.email}</div>
                                            </div>
                                        </div>
                                        <div className="dropdown">
                                            <button className="btn btn-light btn-sm rounded-circle shadow-sm" data-bs-toggle="dropdown">
                                                <i className="bi bi-three-dots"></i>
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end border-0 shadow-lg rounded-4 p-2">
                                                <li><button className="dropdown-item rounded-3 py-2 text-primary"><i className="bi bi-pencil-square me-2"></i> Edit</button></li>
                                                <li><button className="dropdown-item rounded-3 py-2 text-warning" onClick={() => handleOpenResetModal(member)}><i className="bi bi-key me-2"></i> Reset Password</button></li>
                                                <li><button className="dropdown-item rounded-3 py-2 text-danger"><i className="bi bi-trash3 me-2"></i> Remove</button></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {getRoleBadge(member.role)}
                                        <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-2 fw-bold border border-success border-opacity-25">ACTIVE</span>
                                    </div>
                                    <div className="bg-white border rounded-4 p-3 mb-1">
                                        <div className="small mb-2 fw-medium"><i className="bi bi-telephone me-2 text-primary opacity-75"></i>{member.phone_number || 'No Phone Registered'}</div>
                                        <div className="small fw-medium"><i className="bi bi-calendar3 me-2 text-primary opacity-75"></i>Joined: {new Date(member.created_at).toLocaleDateString()}</div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            filteredInvites.map(inv => (
                                <div key={inv.id} className="card border-0 shadow-sm rounded-4 mb-3 p-3 border-start border-warning border-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-warning bg-opacity-10 text-warning rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '45px', height: '45px' }}>
                                            <i className="bi bi-envelope-check"></i>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="fw-bold text-dark text-truncate">{inv.email}</div>
                                            <div className="small text-muted fw-medium text-capitalize">{inv.role} Role</div>
                                        </div>
                                        <div className="badge bg-warning bg-opacity-10 text-warning rounded-pill px-2 py-1 small fw-bold">PENDING</div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-sm btn-primary flex-grow-1 rounded-pill shadow-sm fw-bold py-2" onClick={() => handleResend(inv.id)}><i className="bi bi-arrow-repeat me-1"></i> Resend</button>
                                        <button className="btn btn-sm btn-outline-danger flex-grow-1 rounded-pill shadow-sm fw-bold py-2" onClick={() => handleCancel(inv.id)}>Cancel</button>
                                    </div>
                                </div>
                            ))
                        )}
                        {(activeTab === 'members' ? filteredMembers : filteredInvites).length === 0 && (
                            <div className="text-center py-5">
                                <i className="bi bi-search mb-3 d-block opacity-25" style={{ fontSize: '3rem' }}></i>
                                <h6 className="text-muted fw-bold">No results found for "{searchTerm}"</h6>
                                <p className="small text-muted">Try adjusting your search criteria.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Premium Invite Modal */}
            {showInviteModal && (
                <div className="modal d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} onClick={(e) => e.target === e.currentTarget && setShowInviteModal(false)}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '28px', overflow: 'hidden' }}>
                            <div className="modal-header border-0 pt-4 px-4 pb-0">
                                <div>
                                    <h5 className="modal-title fw-bold">Scale Your Shop Team</h5>
                                    <p className="small text-muted mb-0">Invite members to collaborate in your POS system.</p>
                                </div>
                                <button type="button" className="btn-close shadow-none" onClick={() => setShowInviteModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                {!inviteLink ? (
                                    <form onSubmit={handleInvite}>
                                        <div className="mb-4">
                                            <label className="form-label small fw-bold text-muted text-uppercase ms-1">Email Address</label>
                                            <div className="d-flex align-items-center bg-light rounded-4 px-3 py-2 border border-2 border-transparent transition-all focus-within-primary">
                                                <i className="bi bi-envelope text-primary me-3 fs-5"></i>
                                                <input
                                                    type="email"
                                                    className="form-control bg-transparent border-0 shadow-none p-0 fw-medium search-input-clean"
                                                    placeholder="employee@shopname.com"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    required
                                                    style={{ height: '36px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <label className="form-label small fw-bold text-muted text-uppercase ms-1">Work Role</label>
                                            <div className="row g-2">
                                                {['salesman', 'manager', 'accountant', 'admin'].map(role => (
                                                    <div className="col-6 col-sm-3" key={role}>
                                                        <button 
                                                            type="button"
                                                            className={`btn w-100 py-3 rounded-4 border-2 d-flex flex-column align-items-center justify-content-center transition-all ${formData.role === role ? 'btn-primary border-primary shadow-sm' : 'btn-light border-light text-muted'}`}
                                                            onClick={() => setFormData({...formData, role})}
                                                        >
                                                            <div className="small fw-bold text-capitalize text-truncate w-100">{role}</div>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mt-3 p-3 bg-light rounded-4 border border-dashed border-primary border-opacity-25">
                                                <div className="small text-muted lh-sm">
                                                    <i className="bi bi-info-circle-fill text-primary me-2"></i>
                                                    {formData.role === 'salesman' && 'Restricted to POS operations & basic sales audits.'}
                                                    {formData.role === 'manager' && 'Full control over inventory, categories & customers.'}
                                                    {formData.role === 'accountant' && 'Authorized access to reports, ledger & financials.'}
                                                    {formData.role === 'admin' && 'Unrestricted system-wide owner access.'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-grid pt-2">
                                            <button type="submit" className="btn btn-primary btn-lg rounded-pill fw-bold py-3 shadow-sm" disabled={loading}>
                                                {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Initiating...</> : 'Send Secure Invitation'}
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="text-center py-2">
                                        <div className="mb-4">
                                            <div className="bg-success bg-opacity-10 text-success rounded-circle d-inline-flex align-items-center justify-content-center mb-3 shadow-sm" style={{ width: '80px', height: '80px' }}>
                                                <i className="bi bi-check-all" style={{ fontSize: '3rem' }}></i>
                                            </div>
                                            <h4 className="fw-bold">Invitation Deployed!</h4>
                                            <p className="text-muted small">Access link has been dispatched to <strong>{formData.email}</strong>. You can also share it directly:</p>
                                        </div>
                                        <div className="bg-light p-3 rounded-4 mb-4 position-relative border border-dashed border-primary border-opacity-25">
                                            <div className="small text-muted mb-1 text-start ms-1">Registration Key:</div>
                                            <div className="d-flex align-items-center">
                                                <div className="text-truncate flex-grow-1 fw-bold text-primary small pe-3">{inviteLink}</div>
                                                <button className="btn btn-primary btn-sm rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm" onClick={copyToClipboard} style={{ width: '36px', height: '36px' }}>
                                                    <i className="bi bi-clipboard-plus"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <button className="btn btn-dark w-100 rounded-pill py-3 fw-bold shadow-sm" onClick={() => setShowInviteModal(false)}>Close & Continue</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Password Reset Modal */}
            {showResetModal && selectedMember && (
                <div className="modal d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} onClick={(e) => e.target === e.currentTarget && setShowResetModal(false)}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '28px', overflow: 'hidden' }}>
                            <div className="modal-header border-0 pt-4 px-4 pb-0">
                                <div>
                                    <h5 className="modal-title fw-bold">Reset Employee Password</h5>
                                    <p className="small text-muted mb-0">Set a new secure password for <strong>{selectedMember.name}</strong>.</p>
                                </div>
                                <button type="button" className="btn-close shadow-none" onClick={() => setShowResetModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <form onSubmit={handleResetPassword}>
                                    <div className="mb-3 text-start">
                                        <label className="form-label small fw-bold text-muted text-uppercase ms-1">New Password</label>
                                        <div className="d-flex align-items-center bg-light rounded-4 px-3 py-2 border border-2 border-transparent transition-all focus-within-primary">
                                            <i className="bi bi-shield-lock text-primary me-3 fs-5"></i>
                                            <input
                                                type="password"
                                                className="form-control bg-transparent border-0 shadow-none p-0 fw-medium search-input-clean"
                                                placeholder="Enter new password (min 8 chars)"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                required
                                                style={{ height: '36px' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-4 text-start">
                                        <label className="form-label small fw-bold text-muted text-uppercase ms-1">Confirm New Password</label>
                                        <div className="d-flex align-items-center bg-light rounded-4 px-3 py-2 border border-2 border-transparent transition-all focus-within-primary">
                                            <i className="bi bi-shield-lock-fill text-primary me-3 fs-5"></i>
                                            <input
                                                type="password"
                                                className="form-control bg-transparent border-0 shadow-none p-0 fw-medium search-input-clean"
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                style={{ height: '36px' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-grid pt-2">
                                        <button type="submit" className="btn btn-warning btn-lg rounded-pill fw-bold py-3 text-dark shadow-sm" disabled={resetLoading}>
                                            {resetLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Updating...</> : 'Confirm & Save Password'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                .focus-within-primary:focus-within {
                    border-color: var(--bs-primary) !important;
                    background-color: var(--card-bg) !important;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.1) !important;
                }
                .search-input-clean {
                    background-color: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    color: var(--text-color) !important;
                }
                .active-scale:active {
                    transform: scale(0.98);
                }
                
                /* Dark Mode Support */
                [data-bs-theme="dark"] .bg-white,
                [data-bs-theme="dark"] .bg-light,
                [data-bs-theme="dark"] .card {
                    background-color: var(--card-bg) !important;
                    color: var(--text-color) !important;
                    border-color: var(--border-color) !important;
                }
                [data-bs-theme="dark"] .nav-pills,
                [data-bs-theme="dark"] .bg-light-subtle,
                [data-bs-theme="dark"] .modal-content .bg-light {
                    background-color: rgba(255,255,255,0.02) !important;
                }
                [data-bs-theme="dark"] .table {
                    --bs-table-bg: transparent;
                    --bs-table-color: var(--text-color);
                    --bs-table-border-color: var(--border-color);
                }
                [data-bs-theme="dark"] .text-dark {
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .text-muted {
                    color: var(--text-muted-color) !important;
                }
                [data-bs-theme="dark"] .btn-light {
                    background-color: rgba(255,255,255,0.05) !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .form-control:not(.search-input-clean),
                [data-bs-theme="dark"] .form-select {
                    background-color: rgba(255,255,255,0.03) !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .hover-light:hover {
                    background-color: rgba(255,255,255,0.03) !important;
                }
                [data-bs-theme="dark"] .modal-content {
                    background-color: var(--card-bg) !important;
                    border: 1px solid var(--border-color) !important;
                }
            `}</style>
        </Layout>
    );
};

export default Team;
